/**
 * combat_feel.ts — the rig-free feel budget (combat wave, 2026-09-04).
 *
 * Every avatar in Atlas is an unrigged static mesh, so impact has to be sold
 * without a hit-reaction animation. The levers that need no rig, in the order
 * the practitioners name them and at the DOSE the measurements allow (Kao et
 * al. 2024, n = 1,699: feedback amplification reduces felt competence; Kao
 * 2020, n = 3,018: none and extreme both worse; hit-stop has a measured
 * unpleasant boundary):
 *
 *   hit-stop     ~10 frames — both bodies' VISUALS hold on the contact frame
 *                (the sim never stops: movement is server-authoritative).
 *   camera kick  a small pitch/yaw impulse with a spring return — directional,
 *                legible, brief. Distinct from shake.
 *   shake        Eiserloh's trauma model (shake = trauma², smooth noise on
 *                pitch/yaw/roll, linear decay). OPTIONAL and OFF by default —
 *                Xbox Accessibility Guideline 117 says avoid camera shake or
 *                make it optional. localStorage `atlas:feel-shake` = 'on'.
 *   knock-back   the victim mesh is displaced along the blow and the existing
 *                position lerp returns it (one write, no state).
 *   wind-up      a lean-back + swell on an attacker during its telegraph, a
 *                snap forward at the snapshot instant.
 *   fov          a sprint widens the field of view a few degrees, a slide
 *                drops the eye; both spring back.
 *
 * Pure state + math here; modules apply it (WildsModule for creatures,
 * AtlasAvatarModule for the camera, EnvironmentModule for versonas).
 */
import { padRumble } from './gamepad.js';

// Hit-stop: practitioners put it at 3–12 frames (0.05–0.2 s), scaled by the
// blow's weight and CAPPED — never longer for more damage (the freeze pauses
// only the two bodies, the world keeps running):
//   https://tvtropes.org/pmwiki/pmwiki.php/Main/HitStop
//   https://salivity.github.io/game-development/article/maximizing-game-feel-in-action-game-development
//   https://sourcegaming.info/2015/11/11/thoughts-on-hitstop-sakurais-famitsu-column-vol-490-1/
export const HIT_STOP_MS = 120;            // ≈ 7 frames at 60 fps
export const HIT_STOP_HEAVY_MS = 200;      // ≈ 12 frames — the cap; a heavy blow holds a little longer, never scaled further
export const KICK_TAKEN_PITCH = 0.045;     // rad — being hit nods the camera down
export const KICK_DEALT_PITCH = 0.018;     // rad — landing a blow is a smaller nod
export const KICK_SPRING = 28;             // 1/s² — spring stiffness of the return
export const KICK_DAMP = 9;                // 1/s  — damping (slightly under critical: one overshoot)
export const TRAUMA_TAKEN = 0.35;
export const TRAUMA_DEALT = 0.12;
export const TRAUMA_DECAY = 1.6;           // per second
export const SHAKE_MAX_RAD = 0.06;         // at trauma 1 (never reached by one hit)
export const KNOCKBACK_M = 0.3;
export const WINDUP_LEAN_RAD = 0.14;
export const WINDUP_SWELL = 0.06;
export const LUNGE_MS = 140;
export const SPRINT_FOV_DEG = 6;
export const SLIDE_EYE_DROP_M = 0.45;
/** Crouch (build-now wave 2): the eye rides the shorter capsule (1.6 → 1.05 m). */
export const CROUCH_EYE_DROP_M = 0.55;
/** A dash widens the view a touch — the burst reads before it ends. */
export const DASH_FOV_DEG = 4;
export const FOV_LERP = 8;                 // 1/s

export interface FeelState {
  hitStopUntil: number;
  trauma: number;
  kickPitch: number; kickYaw: number;
  kickVelPitch: number; kickVelYaw: number;
  shakeEnabled: boolean;
  fovOffset: number;
  eyeDrop: number;
  /** counters for diag + probes */
  hitStops: number; kicks: number; knockbacks: number; windups: number;
}

export const feel: FeelState = {
  hitStopUntil: 0, trauma: 0,
  kickPitch: 0, kickYaw: 0, kickVelPitch: 0, kickVelYaw: 0,
  shakeEnabled: false, fovOffset: 0, eyeDrop: 0,
  hitStops: 0, kicks: 0, knockbacks: 0, windups: 0,
};

export function readShakeSetting(): boolean {
  try { return typeof localStorage !== 'undefined' && localStorage.getItem('atlas:feel-shake') === 'on'; } catch { return false; }
}

/** Freeze the attacker's and victim's visuals for a beat. Never stacks longer. */
export function hitStop(now: number, heavy = false): void {
  // The pad speaks here too (2026-09-16): hit-stop is the ONE place a blow
  // registers physically, so a controller feels exactly what the camera does.
  // A heavy stop is a blow TAKEN, which is the loudest thing a pad should say.
  padRumble(heavy ? 'hurt' : 'hit');
  const until = now + (heavy ? HIT_STOP_HEAVY_MS : HIT_STOP_MS);
  if (until > feel.hitStopUntil) feel.hitStopUntil = until;
  feel.hitStops++;
}

export function frozen(now: number): boolean {
  return now < feel.hitStopUntil;
}

/** A camera impulse in radians (+pitch = the view kicks UP, as recoil does;
 *  a blow TAKEN passes a negative pitch — the camera nods). It enters the
 *  spring as VELOCITY, never as a position set, so it always returns — a
 *  spring driven by position is the classic "recoil never recovers" bug:
 *  https://devforum.roblox.com/t/camera-recoil-with-spring-position-does-not-allow-for-recovery/4042466
 *  (spring-damper procedural recoil: https://www.fab.com/listings/299b8cab-b10f-43f9-9819-90603646b9b6) */
export function cameraImpulse(pitch: number, yaw = 0): void {
  feel.kickVelPitch += pitch * KICK_SPRING;
  feel.kickVelYaw += yaw * KICK_SPRING;
  feel.kicks++;
}

export function addTrauma(amount: number): void {
  feel.trauma = Math.min(1, feel.trauma + amount);
}

/** Smooth, deterministic noise in [−1, 1] (two-octave hashed sine — no
 *  Perlin table, good enough for a 60 ms camera wobble). */
export function smoothNoise(t: number, seed: number): number {
  const a = Math.sin(t * 7.13 + seed * 1.7) * 0.6;
  const b = Math.sin(t * 13.71 + seed * 3.1) * 0.3;
  const c = Math.sin(t * 29.3 + seed * 5.9) * 0.1;
  return a + b + c;
}

export interface CameraOffsets { pitch: number; yaw: number; roll: number; fov: number; eyeDrop: number }

/** Advance the springs and decay; returns the offsets to ADD to the camera's
 *  base pose this frame. `tSec` is a monotonic clock for the noise. */
export function stepFeel(dt: number, tSec: number, wantFov: number, wantEyeDrop: number): CameraOffsets {
  // Spring-damper on the kick (semi-implicit Euler, stable at 60 Hz).
  feel.kickVelPitch += (-KICK_SPRING * feel.kickPitch - KICK_DAMP * feel.kickVelPitch) * dt;
  feel.kickPitch += feel.kickVelPitch * dt;
  feel.kickVelYaw += (-KICK_SPRING * feel.kickYaw - KICK_DAMP * feel.kickVelYaw) * dt;
  feel.kickYaw += feel.kickVelYaw * dt;
  if (Math.abs(feel.kickPitch) < 1e-5 && Math.abs(feel.kickVelPitch) < 1e-4) { feel.kickPitch = 0; feel.kickVelPitch = 0; }
  if (Math.abs(feel.kickYaw) < 1e-5 && Math.abs(feel.kickVelYaw) < 1e-4) { feel.kickYaw = 0; feel.kickVelYaw = 0; }
  // Trauma decays linearly; shake = trauma² (Eiserloh).
  feel.trauma = Math.max(0, feel.trauma - TRAUMA_DECAY * dt);
  let sp = 0, sy = 0, sr = 0;
  if (feel.shakeEnabled && feel.trauma > 0) {
    const s = feel.trauma * feel.trauma * SHAKE_MAX_RAD;
    sp = smoothNoise(tSec, 1) * s;
    sy = smoothNoise(tSec, 2) * s;
    sr = smoothNoise(tSec, 3) * s * 0.7;
  }
  // FOV and eye drop chase their targets.
  const k = Math.min(1, FOV_LERP * dt);
  feel.fovOffset += (wantFov - feel.fovOffset) * k;
  feel.eyeDrop += (wantEyeDrop - feel.eyeDrop) * k;
  return { pitch: feel.kickPitch + sp, yaw: feel.kickYaw + sy, roll: sr, fov: feel.fovOffset, eyeDrop: feel.eyeDrop };
}

/** Wind-up pose for an attacker: lean back and swell as the telegraph fills,
 *  then lunge for LUNGE_MS after the snapshot. Returns pitch (rad) + scale. */
export function windupPose(nowMs: number, startMs: number, windupMs: number, lungeAtMs: number): { pitch: number; scale: number } {
  if (lungeAtMs > 0 && nowMs < lungeAtMs + LUNGE_MS) {
    const t = (nowMs - lungeAtMs) / LUNGE_MS; // 0..1
    return { pitch: 0.28 * (1 - t), scale: 1 };
  }
  if (windupMs <= 0) return { pitch: 0, scale: 1 };
  const t = Math.min(1, Math.max(0, (nowMs - startMs) / windupMs));
  const ease = t * t; // slow start, fast finish — the tell sharpens toward the blow
  return { pitch: -WINDUP_LEAN_RAD * ease, scale: 1 + WINDUP_SWELL * ease };
}

/** Deterministic recoil for a gun: first shot then per-shot pattern, in
 *  radians. Shot index seeds a fixed left/right walk so the pattern is
 *  learnable (Treyarch: actual recoil is a discrete tunable, not bloom). */
export function recoilFor(recoil: { first: number; vert: number; horiz: number } | undefined, shotIndex: number): { pitch: number; yaw: number } {
  if (!recoil) return { pitch: 0, yaw: 0 };
  const D = Math.PI / 180;
  const v = (shotIndex === 0 ? recoil.first : recoil.vert) * D;
  const seq = [0.35, -0.6, 0.8, -0.3, 0.55, -0.85, 0.2, -0.45];
  const h = recoil.horiz * D * seq[shotIndex % seq.length];
  return { pitch: v, yaw: h };
}
